import { useState } from 'react';
import { Button, Field, Modal, TextInput } from '@salonomy/ui';
import { useSetPaymentMethod } from '../../data/billing.js';
import { useToast } from '../../lib/toast.js';

/**
 * Add / replace the default payment method (brief 10 §4). Attaching a card NEVER charges — it captures
 * a method on the platform Customer so the next cycle (or a dunning retry) can collect. This is also the
 * trial "add a card" prompt and the dunning "update card" CTA, both routed here.
 *
 * NOTE for the design/launch round: secure card entry is Stripe Elements (the PaymentMethod id is minted
 * client-side by Stripe.js and POSTed here — the raw PAN never touches Salonomy). Stripe.js is not yet
 * mounted in the console, so this captures the resulting PaymentMethod id directly for now and FLAGS it.
 * Wiring Elements is a launch task — see the screen notes. No money math happens here either way.
 */
export function PaymentMethodModal({
  open,
  onClose,
  hasExisting,
}: {
  open: boolean;
  onClose: () => void;
  hasExisting: boolean;
}) {
  const toast = useToast();
  const setPaymentMethod = useSetPaymentMethod();
  const [pmId, setPmId] = useState('');

  function close() {
    setPmId('');
    onClose();
  }

  async function save() {
    const id = pmId.trim();
    if (!id) return;
    try {
      await setPaymentMethod.mutateAsync({ paymentMethodId: id });
      toast.success('Card saved. You won’t be charged until your next cycle.');
      close();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the card.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={hasExisting ? 'Replace your card' : 'Add a card'}
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!pmId.trim() || setPaymentMethod.isPending}>
            {setPaymentMethod.isPending ? 'Saving…' : 'Save card'}
          </Button>
        </>
      }
    >
      <p style={{ margin: '0 0 12px', fontSize: 13.5, lineHeight: 1.5 }}>
        Adding a card doesn’t charge you a penny — it just sets the card we’ll bill at the start of
        your next monthly cycle. No contracts; cancel any time.
      </p>
      <Field
        label="Stripe payment method id"
        hint="Secure card entry (Stripe Elements) is wired for launch — see the design note on this screen. For now, paste the PaymentMethod id Stripe minted (pm_…)."
      >
        <TextInput
          autoFocus
          value={pmId}
          onChange={(e) => setPmId(e.target.value)}
          placeholder="pm_1234…"
        />
      </Field>
    </Modal>
  );
}
